import noop from 'lodash/noop'
import { useState, useEffect, forwardRef } from 'react'
import styled from 'styled-components'
import { TextField } from '@mui/material'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import Icon from '@/components/icon'
import { colors, transitions, zIndex, breakpoints, fontSizes } from '@/styles'
import toId from '@/utils/to-id'
import useMomentLocale from '@/hooks/use-moment-locale'
import Label, { active } from '../floating-label'

const Wrapper = styled.div`
  position: relative;
  ${props =>
    props.isFilled &&
    `
    > label:last-of-type {
      ${active}
    }
  `}
  ${props =>
    props.hasError &&
    `
    .MuiOutlinedInput-root {
      border-color: ${colors.red64} !important;
    }
  `}
`

const Required = styled.span`
  color: ${colors.red64};
`

const secondary = colors.blue16

const large = {
  height: '37px',
  fontSize: fontSizes.regular
}

const medium = {
  height: '33px',
  fontSize: '14px'
}

const responsive = {
  ...medium,
  '@media only screen and (max-width: 767px)': {
    ...large
  }
}

const getInputStyles = size => {
  if (size === 'responsive') return responsive
  if (size === 'medium') return medium
  return large
}

const getCustomStyles = size => {
  return createTheme({
    palette: {
      primary: {
        main: secondary
      }
    },
    zIndex: {
      modal: zIndex.date
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            background: colors.white,
            input: {
              ...getInputStyles(size),
              padding: '12px 8px 0 8px',
              fontFamily: 'Inter, Helvetica, Arial, sans-serif'
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            transition: `${transitions.borderColor}`,
            borderRadius: '6px',
            border: `1.5px solid ${colors.borderField}`,
            cursor: 'pointer',
            '&.Mui-focused': {
              borderColor: `${secondary} !important`
            }
          },
          notchedOutline: {
            border: 'none !important',
            outline: 'none !important'
          }
        }
      },
      MuiPickersToolbar: {
        styleOverrides: {
          toolbar: {
            backgroundColor: secondary
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          flexContainer: {
            backgroundColor: secondary
          }
        }
      },
      MuiPickersDay: {
        styleOverrides: {
          daySelected: {
            backgroundColor: secondary,
            '&:hover': {
              backgroundColor: secondary
            }
          },
          current: {
            color: secondary
          }
        }
      },
      MuiPickersYear: {
        styleOverrides: {
          yearSelected: {
            color: secondary
          }
        }
      }
    }
  })
}

const sx = {
  'button[class*=PrivateDatePickerToolbar]': {
    display: 'none'
  },
  'button[class*=MuiPickersToolbar-penIconButton]': {
    display: 'none'
  }
}

const ClearIcon = styled(Icon)`
  position: absolute;
  right: 8px;
  top: 19px;
  cursor: pointer;
  g {
    fill: ${colors.black};
  }
  ${props =>
    props.size === 'medium' &&
    `
    top: 17px;
  `}
  ${props =>
    props.size === 'responsive' &&
    `
    top: 17px;
    @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
      top: 19px;
    }
  `}
`

const DatePicker = forwardRef(
  (
    {
      id: initialId,
      value,
      placeholder,
      onChange = noop,
      autoOk = true,
      format = 'MM/DD/YYYY',
      children,
      hasError,
      emptyLabel,
      initialFocusedDate: defaultCalendarMonth,
      required,
      size,
      shouldDisableDate,
      iconRight,
      clearIcon = true,
      disabled,
      ...props
    },
    ref
  ) => {
    const [isFilled, setIsFilled] = useState(!!emptyLabel || !!value)
    const momentLocale = useMomentLocale()
    const onChangeWrap = date => {
      setIsFilled(!!emptyLabel || !!date)
      return onChange(date)
    }
    useEffect(() => {
      setIsFilled(!!emptyLabel || !!value)
    }, [value, emptyLabel])
    const id = initialId || toId(placeholder)
    return (
      <Wrapper isFilled={isFilled} hasError={hasError}>
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={momentLocale}>
          <ThemeProvider theme={getCustomStyles(size)}>
            <MuiDatePicker
              ref={ref}
              DialogProps={{ sx, id: `modal-date-${id}` }}
              PopperProps={{ sx, id: `popper-date-${id}` }}
              desktopModeMediaQuery={`@media not all and (max-width: ${breakpoints.ipadMiniMax})`}
              disabledMaskedInput
              autoOk={autoOk}
              inputFormat={format}
              defaultCalendarMonth={defaultCalendarMonth}
              allowSameDateSelection
              onChange={onChangeWrap}
              shouldDisableDate={shouldDisableDate}
              renderInput={({ inputProps, InputProps, children, ...rest }) => (
                <TextField
                  {...rest}
                  fullWidth
                  onClick={
                    disabled ? noop : InputProps?.endAdornment?.props?.children?.props?.onClick
                  }
                  disabled={disabled}
                  inputProps={{ ...inputProps, readOnly: true, placeholder: emptyLabel || '' }}
                >
                  {children}
                </TextField>
              )}
              value={value}
              {...props}
            />
          </ThemeProvider>
        </LocalizationProvider>
        {placeholder && (
          <Label hasError={hasError} size={size}>
            {!iconRight && (
              <ClearIcon id="calendar" width={14} height={14} style={{ pointerEvents: 'none' }} />
            )}
            {placeholder}
            {required && <Required>*</Required>} {children}
          </Label>
        )}

        {!(isFilled && value) && iconRight && clearIcon && (
          <ClearIcon id="calendar" width={14} height={14} style={{ pointerEvents: 'none' }} />
        )}
        {!!(isFilled && value) && clearIcon && (
          <ClearIcon
            id="close"
            width={15}
            height={15}
            onClick={() => onChangeWrap('')}
            size={size}
          />
        )}
      </Wrapper>
    )
  }
)

export default DatePicker
